import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Eye, EyeOff, KeyRound, X } from "lucide-react";
import { PrimaryButton } from "../../components/Buttons";
import { requestPasswordReset, resetPassword } from "../../services/login";

/**
 * Forgotten-password reset for the web.
 *
 * The backend has had a proper OTP flow since v3_92 and the mobile app has used it all along, but
 * the web had no UI for it at all — `requestPasswordReset` / `resetPassword` existed in the service
 * layer with nothing importing them, so a web user simply could not recover an account.
 *
 * Two steps in one dialog. The code now goes to BOTH the email and the WhatsApp number on file
 * (v4_4), which is why the copy names both — a person locked out of their mailbox is a large share
 * of the people who need this at all.
 *
 * The countdown is purely local. The server enforces its own cooldown SILENTLY — it has to keep
 * answering an identical 202 whether or not the account exists, so it cannot tell us to wait
 * without also confirming the username is real. This timer just stops people hammering the button.
 */
const RESEND_SECONDS = 60;

function ForgotPasswordModal({ initialUsername = "", onClose }) {
  const [step, setStep] = useState("REQUEST");
  const [username, setUsername] = useState(initialUsername);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const sendCode = async () => {
    if (!username.trim()) return toast.error("Enter your email or username");
    setBusy(true);
    try {
      await requestPasswordReset(username.trim());
      // Deliberately the same message every time — the server cannot tell us whether the account
      // exists, and neither should this screen.
      toast.success("If that account exists, we've sent a code by email and WhatsApp.");
      setStep("REDEEM");
      setCooldown(RESEND_SECONDS);
    } catch {
      toast.error("Could not start the reset. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async () => {
    if (otp.trim().length !== 6) return toast.error("Enter the 6-digit code");
    if (password.length < 8) return toast.error("Use at least 8 characters for the new password");
    setBusy(true);
    try {
      await resetPassword(username.trim(), otp.trim(), password);
      toast.success("Password updated. You can sign in with it now.");
      onClose?.();
    } catch (error) {
      // Every rejection is the same 400 by design — wrong code, expired, unknown user.
      toast.error(error?.detail || error?.response?.data?.detail || "Invalid or expired code.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <KeyRound size={18} className="text-flamingo-600" />
            <h3 className="text-base font-semibold text-slate-900">Reset your password</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {step === "REQUEST" ? (
          <>
            <p className="mb-4 text-sm text-slate-600">
              Enter your email and we'll send a one-time code to your email address and your WhatsApp
              number.
            </p>
            <label className="text-xs font-medium text-slate-600">Email or username</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              className="mt-1 mb-4 w-full rounded-xl border border-slate-300 py-2 px-3 text-sm"
            />
            <PrimaryButton onClick={sendCode} disabled={busy}>
              {busy ? "Sending…" : "Send code"}
            </PrimaryButton>
          </>
        ) : (
          <>
            <p className="mb-4 text-sm text-slate-600">
              Enter the 6-digit code we sent, and choose a new password. The code expires in 10
              minutes.
            </p>
            <label className="text-xs font-medium text-slate-600">Code</label>
            <input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              className="mt-1 mb-3 w-full rounded-xl border border-slate-300 py-2 px-3 text-sm tracking-[0.35em]"
            />
            <label className="text-xs font-medium text-slate-600">New password</label>
            <div className="relative mt-1 mb-1">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-slate-300 py-2 pl-3 pr-10 text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <p className="mb-4 text-[11px] text-slate-500">At least 8 characters.</p>
            <PrimaryButton onClick={submitNewPassword} disabled={busy}>
              {busy ? "Updating…" : "Set new password"}
            </PrimaryButton>
            <button
              type="button"
              onClick={sendCode}
              disabled={busy || cooldown > 0}
              className="mt-3 w-full text-xs text-slate-500 hover:text-slate-700 disabled:text-slate-300"
            >
              {cooldown > 0 ? `Resend code in ${cooldown}s` : "Didn't get it? Resend code"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ForgotPasswordModal;
